import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import type { RangeGranularity } from '@/utils/granularity.ts';
export type DashboardSection = 'energyManagement' | 'overallPerformance';

export type TDashboardEnergyManagementRecord = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Battery_State_of_Charge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Yield: number;
  time: string;
};

export type TDashboardOverallPerformance = {
  Battery_Charge: number;
  Battery_Discharge: number;
  Consumed: number;
  Consumed_from_Grid: number;
  Fed_to_Grid: number;
  Revenue: number;
  Yield: number;
  revenueChart: { value: number; field: string; time: string }[];
};

type TDashboardBaseResponse = {
  rangeGranularity: RangeGranularity;
};

export type TDashboardFullResponse = TDashboardBaseResponse & {
  energyManagement: TDashboardEnergyManagementRecord[];
  overallPerformance: TDashboardOverallPerformance;
};

export type TDashboardEnergyManagementResponse = TDashboardBaseResponse & {
  energyManagement: TDashboardEnergyManagementRecord[];
};

export type TDashboardOverallPerformanceResponse = TDashboardBaseResponse & {
  overallPerformance: TDashboardOverallPerformance;
};

export type TDashboardResponse =
  | TDashboardFullResponse
  | TDashboardEnergyManagementResponse
  | TDashboardOverallPerformanceResponse;

type SectionResponseMap = {
  energyManagement: TDashboardEnergyManagementResponse;
  overallPerformance: TDashboardOverallPerformanceResponse;
  undefined: TDashboardFullResponse;
};

export type DateRange = {
  startDate: Date;
  endDate: Date | undefined;
  rangeGranularity: RangeGranularity;
};

export async function getOverviewPlant<S extends DashboardSection | undefined = undefined>({
  dateRange,
  viewContext,
  section,
}: {
  dateRange: DateRange;
  viewContext: string;
  section?: S;
}): Promise<SectionResponseMap[(S & string) | 'undefined'] | undefined | never> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('viewContext', viewContext);
    searchParams.set('granularity', dateRange.rangeGranularity);
    searchParams.set('startDate', `${new Date(dateRange.startDate).getTime()}`);
    if (dateRange.endDate) {
      searchParams.set('endDate', `${new Date(dateRange.endDate).getTime()}`);
    }
    if (section) {
      searchParams.set('section', section);
    }

    const { data } = await client.get(`/dashboard/overview?${searchParams.toString()}`);

    if (!data?.body) return undefined;
    return { ...data.body, rangeGranularity: dateRange.rangeGranularity };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(error.message);
    }

    throw error;
  }
}

export function useOverviewQuery<S extends DashboardSection | undefined = undefined>({
  dateRange,
  viewContext,
  section,
  enabled = true,
}: {
  dateRange: DateRange;
  viewContext: string;
  section?: S;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'overview',
      viewContext,
      dateRange.rangeGranularity,
      dateRange.startDate,
      dateRange.endDate,
      section,
    ],
    queryFn: () => getOverviewPlant({ dateRange, viewContext, section }),
    retry: 1,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 1000 * 60,
    enabled,
  });
}
